import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { frontDoorHref, hasLiveSession, intakeUrl, isVaultPath, VAULT_ARRIVAL } from './frontDoor';
import { useRouter } from './context';

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  readonly href: string;
  /** Full-page navigation (static surfaces such as /go/ or /academy/). */
  readonly external?: boolean;
};

/**
 * A real anchor: crawlable, keyboard reachable and openable in a new tab.
 * Plain left clicks on in-app paths are routed without a page load.
 * Links to the vault route follow the front-door rule (frontDoor.ts).
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, external = false, onClick, target, ...rest },
  ref,
) {
  const { navigate } = useRouter();
  const door = frontDoorHref(href, hasLiveSession(), window.location.href);

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented || target === '_blank') return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (isVaultPath(url.pathname)) {
      event.preventDefault();
      if (!hasLiveSession()) window.location.assign(intakeUrl(url.search, url.hash));
      else if (door.rewritten) window.location.assign(VAULT_ARRIVAL);
      else navigate(url.pathname + url.search + url.hash);
      return;
    }
    if (external) return;
    event.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  }

  return (
    <a
      ref={ref}
      href={door.href}
      target={target}
      data-m618-rewritten={door.rewritten ? '' : undefined}
      onClick={handleClick}
      {...rest}
    />
  );
});
